import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Checkbox,
  ChoiceList,
  DropZone,
  FormLayout,
  InlineStack,
  Modal,
  Select,
  Spinner,
  Text,
  TextField,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { ENTRY_LABEL, METHODS, NO_INVOICE_OPTIONS, money, rpcError, toCents, uploadEgresoDoc } from "../../utility/egresos";

const today = () => new Date().toISOString().slice(0, 10);
const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// ------------------------------------------------------------ money movement
// paid_out / returned_in / deposit_out / deposit_in / writeoff
export function MoneyModal({ egreso, kind: initialKind, suggestedCents, onClose, onDone }) {
  // A manual payment may be a refundable deposit instead (sublease guarantee).
  const [kind, setKind] = useState(initialKind);
  const [amount, setAmount] = useState(suggestedCents ? (suggestedCents / 100).toFixed(2) : "");
  const [date, setDate] = useState(today());
  const [method, setMethod] = useState("spei");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const cents = toCents(amount);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const receipt_path = file ? await uploadEgresoDoc(egreso.id, kind, file) : null;
      const { error: err } = await supabase.rpc("add_egreso_entry", {
        p_egreso: egreso.id,
        p: {
          kind,
          amount_cents: cents,
          occurred_at: `${date}T12:00:00-06:00`,
          method: kind === "writeoff" ? null : method,
          description,
          receipt_path,
        },
      });
      if (err) throw new Error(rpcError(err));
      onDone();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const help = {
    paid_out: "Dinero que salió hacia quien se le paga. Se aplica solo a las facturas pendientes, la más antigua primero.",
    returned_in: "Dinero que nos regresó (el cambio de un anticipo). No puede ser más de lo que está por comprobar.",
    deposit_out: "Depósito en garantía que entregamos y debe regresar.",
    deposit_in: "Depósito que nos regresaron.",
    writeoff: "Dinero que ya no va a regresar ni tendrá comprobante. Queda como no deducible.",
  }[kind];

  return (
    <Modal
      open
      onClose={onClose}
      title={ENTRY_LABEL[kind]}
      primaryAction={{ content: "Registrar", onAction: save, loading: saving, disabled: !(cents > 0) }}
      secondaryActions={[{ content: "Cancelar", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}
          <Text tone="subdued">{help}</Text>
          <FormLayout>
            <FormLayout.Group>
              <TextField label="Monto" prefix="$" type="number" value={amount} onChange={setAmount} autoComplete="off" requiredIndicator />
              <TextField label="Fecha" type="date" value={date} onChange={setDate} autoComplete="off" />
            </FormLayout.Group>
            {(initialKind === "paid_out" || initialKind === "deposit_out") && (
              <Checkbox
                label="Es depósito en garantía (debe regresar)"
                checked={kind === "deposit_out"}
                onChange={(v) => setKind(v ? "deposit_out" : "paid_out")}
              />
            )}
            {kind !== "writeoff" && <Select label="Cómo" options={METHODS} value={method} onChange={setMethod} />}
            {method === "efectivo" && kind === "paid_out" && cents > 200000 && (
              <Banner tone="warning">
                Pagar más de $2,000 en efectivo a un proveedor hace la factura no deducible.
              </Banner>
            )}
            <TextField label={kind === "writeoff" ? "Motivo" : "Nota"} value={description} onChange={setDescription} autoComplete="off" />
            {kind !== "writeoff" && (
              <DropZone label="Comprobante de la transferencia" allowMultiple={false} onDrop={(_a, acc) => setFile(acc[0] || null)}>
                {file ? <div style={{ padding: 12 }}><Text>{file.name}</Text></div> : <DropZone.FileUpload />}
              </DropZone>
            )}
          </FormLayout>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

// --------------------------------------------------------------- CFDI picker
// Unassigned received CFDIs (expense_cfdis). Narrowed to what can belong here:
//   rfc         when we buy from the payee, only that issuer's CFDIs (the RPC
//               refuses any other issuer anyway)
//   exactCents  attaching to a ticket: only CFDIs for that amount (±$1)
//   targets     amounts this egreso is waiting for (por comprobar, por pagar,
//               rest of the quote): closest first
// "Mostrar todos" lifts the narrowing for the odd exception. Upload goes
// through gastos_inbox (SAT validity + EFOS).
function CfdiPicker({ rfc, exactCents = null, targets = [], selected, onSelect }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);

  const load = async () => {
    const { data } = await supabase
      .from("expense_cfdis")
      .select("id, uuid, emisor_rfc, emisor_nombre, receptor_rfc, tipo, serie_folio, fecha, total, metodo_pago, forma_pago, sat_estado")
      .eq("status", "unmatched")
      .order("fecha", { ascending: false })
      .limit(300);
    setList(data || []);
  };
  useEffect(() => {
    load();
  }, []);

  const cents = (c) => Math.round(Number(c.total) * 100);
  const fits = (c) =>
    (!rfc || (c.emisor_rfc || "").toUpperCase() === rfc.toUpperCase()) &&
    (exactCents == null || Math.abs(cents(c) - exactCents) <= 100);
  const fitting = useMemo(() => (list || []).filter(fits), [list, rfc, exactCents]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = showAll ? list || [] : fitting;
    const distance = (c) => (targets.length ? Math.min(...targets.map((t) => Math.abs(cents(c) - t))) : 0);
    return base
      .filter(
        (c) =>
          !s ||
          [c.emisor_rfc, c.emisor_nombre, c.serie_folio, c.uuid, String(c.total)].filter(Boolean).some((x) => x.toLowerCase().includes(s))
      )
      .map((c) => ({ c, d: distance(c) }))
      .sort((a, b) => a.d - b.d)
      .map((x) => x.c);
  }, [list, fitting, q, showAll, targets]); // eslint-disable-line react-hooks/exhaustive-deps

  const narrowing = [rfc && `de ${rfc}`, exactCents != null && `por ${money(exactCents)}`].filter(Boolean).join(" ");
  const hiddenCount = (list?.length || 0) - fitting.length;

  const upload = async (files) => {
    setUploading(true);
    setUploadMsg(null);
    try {
      const payload = await Promise.all(files.map(async (f) => ({ name: f.name, base64: await toBase64(f) })));
      const { data, error } = await supabase.functions.invoke("gastos_inbox/upload", { body: { files: payload } });
      if (error) {
        let msg = error.message;
        try {
          msg = (await error.context.json()).error || msg;
        } catch {
          /* keep generic */
        }
        throw new Error(msg);
      }
      const uuids = (data.results || []).map((r) => r.uuid).filter(Boolean);
      await load();
      if (uuids.length) {
        const { data: rows } = await supabase.from("expense_cfdis").select("id, uuid, status").in("uuid", uuids);
        const free = (rows || []).find((r) => r.status === "unmatched");
        if (free) onSelect(free.id);
        else setUploadMsg("El CFDI ya estaba asignado a otro gasto o fue rechazado.");
      } else {
        setUploadMsg("No se encontró un CFDI válido en el archivo.");
      }
    } catch (e) {
      setUploadMsg(e.message);
    }
    setUploading(false);
  };

  return (
    <BlockStack gap="200">
      <DropZone label="Subir XML (y su PDF)" allowMultiple onDrop={(_a, acc) => acc.length && upload(acc)}>
        {uploading ? <div style={{ padding: 12, textAlign: "center" }}><Spinner size="small" /></div> : <DropZone.FileUpload actionHint=".xml, .pdf o .zip" />}
      </DropZone>
      {uploadMsg && <Text tone="caution">{uploadMsg}</Text>}
      <TextField
        label={narrowing && !showAll ? `O elige uno recibido ${narrowing}` : "O elige uno recibido"}
        value={q}
        onChange={setQ}
        placeholder="Nombre, folio o monto"
        autoComplete="off"
        clearButton
        onClearButtonClick={() => setQ("")}
      />
      {list === null ? (
        <Spinner size="small" />
      ) : (
        <div style={{ maxHeight: 280, overflowY: "auto" }}>
          <BlockStack gap="100">
            {shown.slice(0, 40).map((c) => (
              <Box
                key={c.id}
                padding="200"
                borderRadius="200"
                borderWidth="025"
                borderColor={selected === c.id ? "border-focus" : "border"}
                background={selected === c.id ? "bg-surface-selected" : undefined}
              >
                <div role="button" tabIndex={0} style={{ cursor: "pointer" }} onClick={() => onSelect(c.id)} onKeyDown={(e) => e.key === "Enter" && onSelect(c.id)}>
                  <InlineStack align="space-between" blockAlign="center" wrap={false}>
                    <BlockStack gap="050">
                      <Text fontWeight="semibold">{c.emisor_nombre || c.emisor_rfc}</Text>
                      <Text tone="subdued" variant="bodySm">
                        {c.emisor_rfc} · {c.serie_folio || c.uuid?.slice(0, 8)} · {c.fecha ? new Date(c.fecha).toLocaleDateString("es-MX") : ""} · {c.metodo_pago}
                      </Text>
                    </BlockStack>
                    <InlineStack gap="150" blockAlign="center" wrap={false}>
                      {c.tipo === "E" && <Badge tone="info">Nota de crédito</Badge>}
                      {c.sat_estado === "Cancelado" && <Badge tone="critical">Cancelado</Badge>}
                      <Text numeric>{money(Math.round(c.total * 100))}</Text>
                    </InlineStack>
                  </InlineStack>
                </div>
              </Box>
            ))}
            {shown.length === 0 && (
              <Text tone="subdued">
                {narrowing && !showAll
                  ? `No hay CFDIs sin asignar ${narrowing}. Si te lo enviaron, súbelo arriba.`
                  : "No hay CFDIs sin asignar que coincidan."}
              </Text>
            )}
            {narrowing && hiddenCount > 0 && (
              <Button variant="plain" onClick={() => setShowAll((v) => !v)}>
                {showAll ? "Mostrar solo los que corresponden" : `Mostrar todos los CFDI sin asignar (${list.length})`}
              </Button>
            )}
          </BlockStack>
        </div>
      )}
    </BlockStack>
  );
}

// ------------------------------------------------------------- cost (comprobante)
// The payee's own CFDIs first; "Mostrar todos" for a CFDI issued by someone
// else (the AutoZone invoice of a battery a driver bought for us).
const issuerMustMatch = (_egreso, payeeRfc) => !!payeeRfc;

export function CostModal({ egreso, payeeRfc, targets = [], onClose, onDone }) {
  const [mode, setMode] = useState("cfdi");
  const [cfdiId, setCfdiId] = useState(null);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(today());
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [noCfdi, setNoCfdi] = useState(false);
  const [noCfdiReason, setNoCfdiReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const p = { kind: "cost", description };
      if (mode === "cfdi") {
        p.expense_cfdi_id = cfdiId;
      } else {
        p.amount_cents = toCents(amount);
        p.occurred_at = `${date}T12:00:00-06:00`;
        if (file) p.ticket_path = await uploadEgresoDoc(egreso.id, "ticket", file);
        if (noCfdi) p.no_cfdi_reason = noCfdiReason;
      }
      const { error: err } = await supabase.rpc("add_egreso_entry", { p_egreso: egreso.id, p });
      if (err) throw new Error(rpcError(err));
      onDone();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const ok = mode === "cfdi" ? !!cfdiId : toCents(amount) > 0 && (!noCfdi || noCfdiReason.trim());

  return (
    <Modal
      open
      large
      onClose={onClose}
      title="Agregar factura"
      primaryAction={{ content: "Agregar", onAction: save, loading: saving, disabled: !ok }}
      secondaryActions={[{ content: "Cancelar", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}
          <ChoiceList
            title="¿Qué tienes?"
            choices={[
              { label: "Factura (CFDI)", value: "cfdi", helpText: "El monto sale del CFDI. Una nota de crédito (tipo E) resta." },
              { label: "Ticket o recibo, sin CFDI todavía", value: "ticket", helpText: "Queda pendiente; cuando llegue la factura se la asignas." },
            ]}
            selected={[mode]}
            onChange={([v]) => setMode(v)}
          />
          {mode === "cfdi" ? (
            <CfdiPicker
              rfc={issuerMustMatch(egreso, payeeRfc) ? payeeRfc : null}
              targets={targets}
              selected={cfdiId}
              onSelect={setCfdiId}
            />
          ) : (
            <FormLayout>
              <FormLayout.Group>
                <TextField label="Monto" prefix="$" type="number" value={amount} onChange={setAmount} autoComplete="off" requiredIndicator />
                <TextField label="Fecha" type="date" value={date} onChange={setDate} autoComplete="off" />
              </FormLayout.Group>
              <DropZone label="Foto del ticket" allowMultiple={false} onDrop={(_a, acc) => setFile(acc[0] || null)}>
                {file ? <div style={{ padding: 12 }}><Text>{file.name}</Text></div> : <DropZone.FileUpload />}
              </DropZone>
              <ChoiceList
                title=""
                titleHidden
                allowMultiple
                choices={[{ label: "No habrá factura", value: "x", helpText: "Se registra como no deducible" }]}
                selected={noCfdi ? ["x"] : []}
                onChange={(v) => setNoCfdi(v.length > 0)}
              />
              {noCfdi && <TextField label="¿Por qué?" value={noCfdiReason} onChange={setNoCfdiReason} autoComplete="off" requiredIndicator />}
            </FormLayout>
          )}
          <TextField label="Descripción" value={description} onChange={setDescription} autoComplete="off" />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

// A ticket line whose CFDI arrived later.
export function AttachCfdiModal({ line, egreso, payeeRfc, onClose, onDone }) {
  const [cfdiId, setCfdiId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const save = async () => {
    setSaving(true);
    const { error: err } = await supabase.rpc("attach_egreso_cfdi", { p_entry: line.entry_id, p_cfdi: cfdiId });
    if (err) {
      setError(rpcError(err));
      return setSaving(false);
    }
    onDone();
  };
  return (
    <Modal
      open
      large
      onClose={onClose}
      title={`Factura del ticket de ${money(line.cost_cents)}`}
      primaryAction={{ content: "Asignar", onAction: save, loading: saving, disabled: !cfdiId }}
      secondaryActions={[{ content: "Cancelar", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}
          <CfdiPicker
            rfc={issuerMustMatch(egreso, payeeRfc) ? payeeRfc : null}
            exactCents={line.cost_cents}
            selected={cfdiId}
            onSelect={setCfdiId}
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

// ------------------------------------------------------------- car split
export function TargetsModal({ line, egreso, current, onClose, onDone }) {
  const [rows, setRows] = useState(
    current.length
      ? current.map((t) => ({ plate: t.car?.plate || t.sublease_plate || "", car_id: t.car_id, reservation_id: t.reservation_id || "", amount: (t.amount_cents / 100).toFixed(2) }))
      : [{ plate: egreso.car?.plate || egreso.sublease_plate || "", car_id: egreso.car_id, reservation_id: egreso.reservation_id || "", amount: (line.cost_cents / 100).toFixed(2) }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const sum = rows.reduce((s, r) => s + (toCents(r.amount) || 0), 0);
  const set = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v, ...(k === "plate" ? { car_id: null } : {}) } : r)));

  const save = async () => {
    setSaving(true);
    setError(null);
    // Resolve plates to fleet cars; unknown plates stay as subleased plates.
    const plates = [...new Set(rows.filter((r) => !r.car_id && r.plate).map((r) => r.plate.trim().toUpperCase()))];
    const { data: cars } = plates.length ? await supabase.from("cars").select("id, plate").in("plate", plates) : { data: [] };
    const byPlate = Object.fromEntries((cars || []).map((c) => [c.plate.toUpperCase(), c.id]));
    const targets = rows.map((r) => {
      const plate = r.plate.trim().toUpperCase();
      const car_id = r.car_id || byPlate[plate] || null;
      return {
        car_id,
        sublease_plate: car_id ? null : plate || null,
        reservation_id: r.reservation_id ? Number(r.reservation_id) : null,
        amount_cents: toCents(r.amount),
      };
    });
    const { error: err } = await supabase.rpc("set_egreso_cost_targets", { p_entry: line.entry_id, p_targets: targets });
    if (err) {
      setError(rpcError(err));
      return setSaving(false);
    }
    onDone();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="¿A qué autos corresponde?"
      primaryAction={{ content: "Guardar", onAction: save, loading: saving, disabled: sum !== line.cost_cents }}
      secondaryActions={[{ content: "Cancelar", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}
          <Text tone="subdued">Reparte el costo de {money(line.cost_cents)} entre autos o rentas (p. ej. una factura semanal de varios subarrendamientos).</Text>
          {rows.map((r, i) => (
            <FormLayout.Group key={i} condensed>
              <TextField label="Placa" value={r.plate} onChange={(v) => set(i, "plate", v.toUpperCase())} autoComplete="off" />
              <TextField label="Reservación #" value={String(r.reservation_id || "")} onChange={(v) => set(i, "reservation_id", v.replace(/\D/g, ""))} autoComplete="off" />
              <TextField label="Monto" prefix="$" type="number" value={r.amount} onChange={(v) => set(i, "amount", v)} autoComplete="off" />
            </FormLayout.Group>
          ))}
          <InlineStack align="space-between">
            <Button onClick={() => setRows((rs) => [...rs, { plate: "", car_id: null, reservation_id: "", amount: "" }])}>Agregar auto</Button>
            <Text tone={sum === line.cost_cents ? "success" : "critical"}>
              {money(sum)} de {money(line.cost_cents)}
            </Text>
          </InlineStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

// ---------------------------------------------------------- deductibility
export function DeductibilityModal({ line, onClose, onDone }) {
  const [status, setStatus] = useState(line.deductible_status === "no_deducible" ? "deducible" : "no_deducible");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const save = async (value) => {
    setSaving(true);
    const { error: err } = await supabase.rpc("set_egreso_deductibility", {
      p_entry: line.entry_id,
      p_status: value,
      p_reason: value ? reason : null,
    });
    if (err) {
      setError(rpcError(err));
      return setSaving(false);
    }
    onDone();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="Deducibilidad"
      primaryAction={{ content: "Guardar", onAction: () => save(status), loading: saving, disabled: !reason.trim() }}
      secondaryActions={[
        { content: "Volver a automático", onAction: () => save(null) },
        { content: "Cancelar", onAction: onClose },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}
          <Text tone="subdued">Automático: {line.deductible_reason || "deducible"}.</Text>
          <Select
            label="Marcar como"
            options={[
              { label: "Deducible", value: "deducible" },
              { label: "No deducible", value: "no_deducible" },
            ]}
            value={status}
            onChange={setStatus}
          />
          <TextField label="Motivo" value={reason} onChange={setReason} autoComplete="off" />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

// ------------------------------------------------------------ small prompts
export function ReasonModal({ title, label, primary, destructive, onClose, onConfirm, required = true, help }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const go = async () => {
    setSaving(true);
    const err = await onConfirm(reason);
    if (err) {
      setError(err);
      setSaving(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      primaryAction={{ content: primary, onAction: go, loading: saving, destructive, disabled: required && !reason.trim() }}
      secondaryActions={[{ content: "Cancelar", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}
          {help && <Text tone="subdued">{help}</Text>}
          <TextField label={label} value={reason} onChange={setReason} autoComplete="off" requiredIndicator={required} />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

// Mark an egreso as one that never takes an invoice (card payment, taxes…).
export function NoInvoiceModal({ egresoId, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const save = async () => {
    setSaving(true);
    const { error: err } = await supabase.rpc("set_egreso_no_invoice", { p_egreso: egresoId, p_reason: reason, p_note: note });
    if (err) {
      setError(rpcError(err));
      return setSaving(false);
    }
    onDone();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="No lleva factura"
      primaryAction={{ content: "Guardar", onAction: save, loading: saving, disabled: !reason || (reason === "otro" && !note.trim()) }}
      secondaryActions={[{ content: "Cancelar", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}
          {reason && (
            <Text tone="subdued">
              {reason === "no_deducible"
                ? "Deja de esperar factura. Lo pagado cuenta como gasto no deducible."
                : "Deja de esperar factura y no cuenta como gasto en los reportes. Un pago de tarjeta no es un gasto: cada cargo de la tarjeta tiene su propia factura."}
            </Text>
          )}
          <Select label="Motivo" options={NO_INVOICE_OPTIONS} value={reason} onChange={setReason} requiredIndicator />
          {reason === "otro" && <TextField label="¿Por qué?" value={note} onChange={setNote} autoComplete="off" requiredIndicator />}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
